import axios, { type AxiosInstance, type AxiosRequestConfig, type AxiosResponse } from 'axios';

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  details?: unknown;
  meta?: {
    page?: number;
    limit?: number;
    total?: number;
    totalPages?: number;
  };
}

// =============================================================================
// Session Types
// =============================================================================

export interface SessionUser {
  id: string;
  email: string;
  name: { first: string; last: string };
  roles: string[];
  emailVerified: boolean;
}

export interface CreateSessionResponse {
  sessionToken: string;
  expiresAt: string;
  user: SessionUser;
}

export interface SessionInfo {
  id: string;
  email: string;
  roles: string[];
  emailVerified: boolean;
  deviceInfo: {
    userAgent?: string;
    ipAddress?: string;
    deviceName?: string;
  };
  createdAt: string;
  expiresAt: string;
  lastActivityAt: string;
}

export interface UserSession {
  id: string;
  deviceInfo: {
    userAgent?: string;
    ipAddress?: string;
    deviceName?: string;
  };
  createdAt: string;
  lastActivityAt: string;
  expiresAt: string;
  isCurrent: boolean;
}

// =============================================================================
// API Client
// =============================================================================

class ApiClient {
  private client: AxiosInstance;
  private sessionRefreshPromise: Promise<boolean> | null = null;

  constructor() {
    this.client = axios.create({
      baseURL: import.meta.env.VITE_API_URL || '/api',
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  private setupInterceptors(): void {
    // Request interceptor
    this.client.interceptors.request.use(
      (config) => {
        // Prefer session token over Firebase token
        const sessionToken = localStorage.getItem('sessionToken');
        const authToken = localStorage.getItem('authToken');

        if (sessionToken) {
          config.headers.Authorization = `Bearer ${sessionToken}`;
        } else if (authToken) {
          config.headers.Authorization = `Bearer ${authToken}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor
    this.client.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        // Handle session expired
        if (
          error.response?.status === 401 &&
          error.response?.data?.error?.code === 'SESSION_EXPIRED' &&
          !originalRequest._retry
        ) {
          originalRequest._retry = true;

          // Try to refresh the session
          const refreshed = await this.tryRefreshSession();
          if (refreshed) {
            // Retry the original request with new token
            const sessionToken = localStorage.getItem('sessionToken');
            originalRequest.headers.Authorization = `Bearer ${sessionToken}`;
            return this.client(originalRequest);
          }
        }

        // Handle general unauthorized
        if (error.response?.status === 401) {
          this.clearSession();
          // Redirect to login if not already there
          if (window.location.pathname !== '/login') {
            window.location.href = '/login';
          }
        }

        return Promise.reject(error);
      }
    );
  }

  // =============================================================================
  // Session Management
  // =============================================================================

  /**
   * Create a new backend session from a Firebase token
   */
  async createSession(
    firebaseToken: string,
    deviceName?: string
  ): Promise<ApiResponse<CreateSessionResponse>> {
    const response = await this.post<CreateSessionResponse>('/auth/session', {
      firebaseToken,
      deviceName,
    });

    if (response.success && response.data) {
      this.setSessionToken(response.data.sessionToken);
      this.setSessionExpiresAt(response.data.expiresAt);
    }

    return response;
  }

  /**
   * Refresh the current session with a new Firebase token
   */
  async refreshSession(firebaseToken: string): Promise<ApiResponse<CreateSessionResponse>> {
    const response = await this.post<CreateSessionResponse>('/auth/session/refresh', {
      firebaseToken,
    });

    if (response.success && response.data) {
      this.setSessionToken(response.data.sessionToken);
      this.setSessionExpiresAt(response.data.expiresAt);
    }

    return response;
  }

  /**
   * Get current session info
   */
  async getSession(): Promise<ApiResponse<SessionInfo>> {
    return this.get<SessionInfo>('/auth/session');
  }

  /**
   * Get all user sessions
   */
  async getAllSessions(): Promise<ApiResponse<{ sessions: UserSession[]; total: number }>> {
    return this.get<{ sessions: UserSession[]; total: number }>('/auth/session/all');
  }

  /**
   * Revoke current session (logout)
   */
  async revokeSession(): Promise<ApiResponse<{ message: string }>> {
    const response = await this.delete<{ message: string }>('/auth/session');
    this.clearSession();
    return response;
  }

  /**
   * Revoke all user sessions
   */
  async revokeAllSessions(): Promise<ApiResponse<{ message: string; count: number }>> {
    const response = await this.delete<{ message: string; count: number }>('/auth/session/all');
    this.clearSession();
    return response;
  }

  /**
   * Try to refresh the session using a fresh Firebase token
   * Returns true if refresh was successful
   */
  private async tryRefreshSession(): Promise<boolean> {
    // Prevent multiple simultaneous refresh attempts
    if (this.sessionRefreshPromise) {
      return this.sessionRefreshPromise;
    }

    this.sessionRefreshPromise = this.doRefreshSession();

    try {
      return await this.sessionRefreshPromise;
    } finally {
      this.sessionRefreshPromise = null;
    }
  }

  private async doRefreshSession(): Promise<boolean> {
    try {
      // This requires the Firebase Auth composable to get a fresh token
      // We'll emit an event that the composable can listen to
      const event = new CustomEvent('session-refresh-needed');
      window.dispatchEvent(event);

      // Wait for the refresh to complete (with timeout)
      return new Promise((resolve) => {
        const timeout = setTimeout(() => resolve(false), 5000);

        const handler = (e: Event) => {
          clearTimeout(timeout);
          window.removeEventListener('session-refreshed', handler);
          resolve((e as CustomEvent<boolean>).detail);
        };

        window.addEventListener('session-refreshed', handler);
      });
    } catch {
      return false;
    }
  }

  // =============================================================================
  // Token Storage
  // =============================================================================

  setSessionToken(token: string): void {
    localStorage.setItem('sessionToken', token);
  }

  getSessionToken(): string | null {
    return localStorage.getItem('sessionToken');
  }

  setSessionExpiresAt(expiresAt: string): void {
    localStorage.setItem('sessionExpiresAt', expiresAt);
  }

  getSessionExpiresAt(): Date | null {
    const expiresAt = localStorage.getItem('sessionExpiresAt');
    return expiresAt ? new Date(expiresAt) : null;
  }

  isSessionExpired(): boolean {
    const expiresAt = this.getSessionExpiresAt();
    if (!expiresAt) return true;
    return expiresAt <= new Date();
  }

  hasSession(): boolean {
    return !!this.getSessionToken() && !this.isSessionExpired();
  }

  clearSession(): void {
    localStorage.removeItem('sessionToken');
    localStorage.removeItem('sessionExpiresAt');
  }

  // Legacy auth token methods (for backward compatibility)
  setAuthToken(token: string): void {
    localStorage.setItem('authToken', token);
  }

  getAuthToken(): string | null {
    return localStorage.getItem('authToken');
  }

  clearAuthToken(): void {
    localStorage.removeItem('authToken');
  }

  // =============================================================================
  // HTTP Methods
  // =============================================================================

  async get<T>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const response: AxiosResponse<ApiResponse<T>> = await this.client.get(url, config);
    return response.data;
  }

  async post<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const response: AxiosResponse<ApiResponse<T>> = await this.client.post(url, data, config);
    return response.data;
  }

  async put<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const response: AxiosResponse<ApiResponse<T>> = await this.client.put(url, data, config);
    return response.data;
  }

  async patch<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const response: AxiosResponse<ApiResponse<T>> = await this.client.patch(url, data, config);
    return response.data;
  }

  async delete<T>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const response: AxiosResponse<ApiResponse<T>> = await this.client.delete(url, config);
    return response.data;
  }
}

export const api = new ApiClient();
